// src/components/Teacher/TeacherFeedback.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface FeedbackStory {
    storyId: string;
    title: string;
    studentId: string;
    studentName: string;
    classId: string;
    className: string;
    submittedAt: any;
    wordCount: number;
    hasFeedback: boolean;
    assignmentId?: string;
    assignmentTitle?: string;
}
 
const getRelativeTime = (ts: any): string => {
    if (!ts?.toDate) return '—';
    const diff = Date.now() - ts.toDate().getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return 'Just now';
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return '1d ago';
    if (days < 30)  return `${days}d ago`;
    return ts.toDate().toLocaleDateString();
};
 
// ── Main component ─────────────────────────────────────────────────────────────
const TeacherFeedback: React.FC = () => {
    const navigate        = useNavigate();
    const { currentUser } = useAuth();
 
    const [stories, setStories]       = useState<FeedbackStory[]>([]);
    const [loading, setLoading]       = useState(true);
    const [filterClass, setFilterClass] = useState('all');
    const [filterStatus, setFilterStatus] = useState<'needs' | 'given' | 'all'>('needs');
    const [classes, setClasses]       = useState<{ id: string; name: string }[]>([]);
    const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    const load = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            // Get all teacher's classes
            const classSnap = await getDocs(
                query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid))
            );
            const classMap: Record<string, string> = {};
            classSnap.docs.forEach(d => { classMap[d.id] = d.data().name; });
            const classIds = Object.keys(classMap);
            setClasses(classSnap.docs.map(d => ({ id: d.id, name: d.data().name })));
 
            if (classIds.length === 0) { setLoading(false); return; }
 
            // Fetch published stories across all classes (chunked for Firestore 'in' limit)
            const chunks: string[][] = [];
            for (let i = 0; i < classIds.length; i += 10) chunks.push(classIds.slice(i, i + 10));
 
            const allStories: any[] = [];
            for (const chunk of chunks) {
                const snap = await getDocs(query(
                    collection(db, 'stories'),
                    where('classId', 'in', chunk),
                    where('status', '==', 'published')
                ));
                snap.docs.forEach(d => allStories.push({ id: d.id, ...d.data() }));
            }
 
            // Resolve student names
            const studentIds = Array.from(new Set(allStories.map((s: any) => s.userId).filter(Boolean)));
            const studentNames: Record<string, string> = {};
            await Promise.all(studentIds.map(async (uid: string) => {
                const uDoc = await getDoc(doc(db, 'users', uid));
                studentNames[uid] = uDoc.exists() ? uDoc.data().displayName || 'Student' : 'Student';
            }));
 
            // Resolve assignment titles
            const assignmentIds = Array.from(new Set(
                allStories.map((s: any) => s.assignmentId).filter(Boolean)
            ));
            const assignmentTitles: Record<string, string> = {};
            await Promise.all(assignmentIds.map(async (aid: string) => {
                const aDoc = await getDoc(doc(db, 'assignments', aid));
                assignmentTitles[aid] = aDoc.exists() ? aDoc.data().title || 'Assignment' : 'Assignment';
            }));
 
            // Check teacherFeedback collection for each story
const mappedWithFeedback = await Promise.all(allStories.map(async (s: any) => {
    const chapters = s.content?.chapters || [];
    const wordCount = chapters.reduce((total: number, ch: any) => {
        if (ch.blocks) {
            return total + ch.blocks.reduce((t: number, b: any) => {
                if (b.type !== 'text' || !b.content) return t;
                return t + b.content.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(Boolean).length;
            }, 0);
        }
        return total + (ch.content?.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(Boolean).length || 0);
    }, 0);

    // Check teacherFeedback collection
    const feedbackDoc = await getDoc(doc(db, 'teacherFeedback', s.id));
    const hasFeedback = feedbackDoc.exists() && !!(feedbackDoc.data()?.publicComment);

    return {
        storyId:        s.id,
        title:          s.title || 'Untitled',
        studentId:      s.userId,
        studentName:    studentNames[s.userId] || 'Student',
        classId:        s.classId,
        className:      classMap[s.classId] || 'Class',
        submittedAt:    s.submittedAt,
        wordCount,
        hasFeedback,
        assignmentId:    s.assignmentId,
        assignmentTitle: s.assignmentId ? assignmentTitles[s.assignmentId] : undefined,
    };
}));

mappedWithFeedback.sort((a, b) => {
    if (a.hasFeedback !== b.hasFeedback) return a.hasFeedback ? 1 : -1;
    return (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0);
});

setStories(mappedWithFeedback);
        } catch (err) {
            console.error('Error loading feedback stories:', err);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);
 
    useEffect(() => { load(); }, [load]);
 
    const filtered = stories.filter(s => {
        const classMatch  = filterClass === 'all' || s.classId === filterClass;
        const statusMatch = filterStatus === 'all'
            || (filterStatus === 'needs' && !s.hasFeedback)
            || (filterStatus === 'given' && s.hasFeedback);
        return classMatch && statusMatch;
    });
 
    const needsCount = stories.filter(s => !s.hasFeedback).length;
    const givenCount = stories.filter(s => s.hasFeedback).length;
 
    const pillBtn = (label: string, value: string, current: string, setter: (v: any) => void, accent: string) => (
        <button
            key={value}
            onClick={() => setter(value)}
            style={{ padding: '8px 18px', borderRadius: '9999px', border: 'none', background: current === value ? accent : C.surfaceHigh, color: current === value ? '#fff' : C.onSurfaceVariant, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}
        >
            {label}
        </button>
    );
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ marginBottom: '32px' }}>
                    {/* Breadcrumb */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <button onClick={() => navigate('/dashboard')}
                            style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                            Dashboard
                        </button>
                        <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                        <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '600' }}>Feedback</span>
                    </div>
 
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 6px 0' }}>
                        Feedback
                    </h1>
                    <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
                        Published stories from your students waiting for your comments.
                    </p>
                </div>
 
                {/* ── Stat pills ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '28px' }}>
                    <div style={{ background: C.errorContainer, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.error }}>{needsCount}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.error, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Needs Feedback</span>
                    </div>
                    <div style={{ background: C.secondaryContainer, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.secondary }}>{givenCount}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Feedback Given</span>
                    </div>
                    <div style={{ background: C.surfaceHighest, borderRadius: '14px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onSurfaceVariant }}>{stories.length}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Submissions</span>
                    </div>
                </div>
 
                {/* ── Filters ─────────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {pillBtn('Needs Feedback', 'needs', filterStatus, setFilterStatus, C.error)}
                        {pillBtn('Feedback Given', 'given', filterStatus, setFilterStatus, C.secondary)}
                        {pillBtn('All', 'all', filterStatus, setFilterStatus, C.onSurface)}
                    </div>
                    {classes.length > 1 && (
                        <select
                            value={filterClass}
                            onChange={e => setFilterClass(e.target.value)}
                            style={{ padding: '8px 14px', borderRadius: '9999px', border: `1.5px solid ${C.outlineVariant}`, background: C.surfaceLowest, color: C.onSurface, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none' }}
                        >
                            <option value="all">All Classes</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                </div>
 
                {/* ── Story list ───────────────────────────────────────────── */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: C.onSurfaceVariant }}>
                        Loading stories...
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '60px 32px', textAlign: 'center', border: `1px solid ${C.surfaceHigh}` }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>
                            {filterStatus === 'needs' ? '🎉' : '📭'}
                        </div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {filterStatus === 'needs'
                                ? 'All caught up!'
                                : filterStatus === 'given'
                                ? 'No feedback given yet'
                                : 'No submissions yet'}
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
                            {filterStatus === 'needs'
                                ? 'Every published story has received feedback. Great work!'
                                : 'Stories will appear here once students publish their work.'}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filtered.map(story => (
                            <div
                                key={story.storyId}
                                style={{
                                    background: C.surfaceLowest,
                                    borderRadius: '18px',
                                    padding: '20px 24px',
                                    border: `1px solid ${story.hasFeedback ? C.surfaceHigh : C.error + '40'}`,
                                    boxShadow: story.hasFeedback ? '0 2px 8px rgba(0,0,0,0.04)' : `0 2px 12px ${C.error}10`,
                                    display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                                    transition: 'box-shadow 0.2s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = story.hasFeedback ? '0 2px 8px rgba(0,0,0,0.04)' : `0 2px 12px ${C.error}10`; }}
                            >
                                {/* Left: avatar + info */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: '200px' }}>
                                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '900', fontSize: '1rem', flexShrink: 0 }}>
                                        {story.studentName.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {story.title}
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            <span>✍️ {story.studentName}</span>
                                            <span>📚 {story.className}</span>
                                            {story.assignmentTitle && <span>📋 {story.assignmentTitle}</span>}
                                        </div>
                                    </div>
                                </div>
 
                                {/* Middle: meta */}
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.1rem', color: C.primary }}>{story.wordCount > 0 ? story.wordCount.toLocaleString() : '—'}</div>
                                        <div style={{ fontSize: '0.62rem', fontWeight: '700', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.5px' }}>words</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, fontWeight: '500' }}>{getRelativeTime(story.submittedAt)}</div>
                                        <div style={{ fontSize: '0.62rem', fontWeight: '700', color: C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '0.5px' }}>submitted</div>
                                    </div>
                                </div>
 
                                {/* Right: status + CTA */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                    {story.hasFeedback ? (
                                        <span style={{ background: C.secondaryContainer, color: C.secondary, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            ✅ Done
                                        </span>
                                    ) : (
                                        <span style={{ background: C.errorContainer, color: C.error, padding: '3px 10px', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            ⚠️ Pending
                                        </span>
                                    )}
                                    <button
                                        onClick={() => navigate(`/teacher/class/${story.classId}/story/${story.storyId}`)}
                                        style={{ background: story.hasFeedback ? C.surfaceLow : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, color: story.hasFeedback ? C.onSurfaceVariant : '#fff', border: 'none', borderRadius: '9999px', padding: '8px 18px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s', whiteSpace: 'nowrap', boxShadow: story.hasFeedback ? 'none' : `0 4px 12px ${C.primary}40` }}
                                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                    >
                                        {story.hasFeedback ? 'View →' : 'Give Feedback →'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
 
export default TeacherFeedback;